// src/services/ColombiaMapService.ts
import type { Feature, FeatureCollection, Geometry, Point, GeoJsonProperties } from "geojson";

// Service types that can handle mixed geometry types from API
export type GeoJsonFeature = Feature<Geometry | null, GeoJsonProperties>;
export type GeoJsonData = FeatureCollection<Geometry | null, GeoJsonProperties>;

type BoundsLike =
  | { getWest: () => number; getSouth: () => number; getEast: () => number; getNorth: () => number }
  | { _sw: { lng: number; lat: number }; _ne: { lng: number; lat: number } }
  | [number, number, number, number];

type Listener<T> = (v: T) => void;

class ColombiaMapService {
  private _geojson: GeoJsonData | null = null;
  private _filtered: GeoJsonData = { type: "FeatureCollection", features: [] };

  private _population: string[] = [];
  private _services: string[] = [];
  private _days: string[] = [];
  private _bounds: [number, number, number, number] | null = null; // [w,s,e,n]

  private _selected: GeoJsonFeature | null = null;
  private _currentPos: { lat: number; lng: number } | null = null;

  private filteredSubs = new Set<Listener<GeoJsonData>>();
  private selectedSubs = new Set<Listener<GeoJsonFeature | null>>();
  private viewListSubs = new Set<Listener<boolean>>();
  private alertSubs = new Set<Listener<string | null>>();
  private currentPosSubs = new Set<Listener<{ lat: number; lng: number } | null>>();

  // ===== dataset =====
  setGeoJsonData(data: GeoJsonData) {
    this._geojson = data;
    this.recalcAndEmit();
  }
  getGeoJsonData(): GeoJsonData | null {
    return this._geojson;
  }

  // Load GeoJSON data from file
  async loadGeoJsonData(): Promise<void> {
    try {
      const response = await fetch('/assets/geojson/BBDD_20260202.geojson');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data: GeoJsonData = await response.json();
      this.setGeoJsonData(data);
    } catch (error) {
      console.error('Error loading GeoJSON data:', error);
      // Fallback to empty data
      this.setGeoJsonData({ type: 'FeatureCollection', features: [] });
    }
  }

  // ===== subscribes =====
  subscribeToFilteredGeoJson(cb: Listener<GeoJsonData>): () => void {
    this.filteredSubs.add(cb);
    cb(this._filtered);
    return () => void this.filteredSubs.delete(cb);
  }
  subscribeToSelectedItem(cb: Listener<GeoJsonFeature | null>): () => void {
    this.selectedSubs.add(cb);
    cb(this._selected);
    return () => void this.selectedSubs.delete(cb);
  }
  subscribeToViewList(cb: Listener<boolean>): () => void {
    this.viewListSubs.add(cb);
    return () => void this.viewListSubs.delete(cb);
  }
  subscribeToShowAlert(cb: Listener<string | null>): () => void {
    this.alertSubs.add(cb);
    return () => void this.alertSubs.delete(cb);
  }
  subscribeToCurrentPosition(cb: Listener<{ lat: number; lng: number } | null>): () => void {
    this.currentPosSubs.add(cb);
    cb(this._currentPos);
    return () => void this.currentPosSubs.delete(cb);
  }

  // ===== acciones =====
  setSelectItem(f: GeoJsonFeature | null) {
    this._selected = f;
    this.selectedSubs.forEach((fn) => fn(this._selected));
  }
  selectItemById(id: string | number) {
    if (!this._geojson) return;
    const hit = this._geojson.features.find((f) => `${f.properties?.ID}` === `${id}`);
    if (hit) this.setSelectItem(hit);
  }
  openViewList() {
    this.viewListSubs.forEach((fn) => fn(true));
  }
  setShowAlert(text: string) {
    this.alertSubs.forEach((fn) => fn(text));
  }
  setCurrentPosition(lat: number, lng: number) {
    this._currentPos = { lat, lng };
    this.currentPosSubs.forEach((fn) => fn(this._currentPos));
  }

  setPopulationFilter(arr: string[]) { this._population = arr; }
  setServiceFilter(arr: string[]) { this._services = arr; }
  setDaysFilter(arr: string[]) { this._days = arr; }

  setBoundFilter(bounds: BoundsLike) {
    if (Array.isArray(bounds) && bounds.length === 4) {
      this._bounds = bounds as [number, number, number, number];
    } else if ("getWest" in (bounds as any)) {
      this._bounds = [
        (bounds as any).getWest(),
        (bounds as any).getSouth(),
        (bounds as any).getEast(),
        (bounds as any).getNorth(),
      ];
    } else if ("_sw" in (bounds as any)) {
      this._bounds = [
        (bounds as any)._sw.lng,
        (bounds as any)._sw.lat,
        (bounds as any)._ne.lng,
        (bounds as any)._ne.lat,
      ];
    }
    this.recalcAndEmit();
  }

  clearFilter() {
    this._population = [];
    this._services = [];
    this._days = [];
    this._bounds = null;
    this.recalcAndEmit();
  }

  recalcAndEmit() {
    const feats = (this._geojson?.features ?? []).filter((f) => this.passFilters(f));
    this._filtered = { type: "FeatureCollection", features: feats };
    this.filteredSubs.forEach((fn) => fn(this._filtered));
  }

  private passFilters(f: GeoJsonFeature): boolean {
    const props = f.properties || {};
    const getStr = (k: string) => (props[k] ?? "").toString().toLowerCase();

    if (this._population.length) {
      const hay = getStr("Población atendida");
      if (!this._population.every((p) => hay.includes(p.toLowerCase()))) return false;
    }
    if (this._services.length) {
      const hay = getStr("Servicio Ofrecido");
      if (!this._services.some((s) => hay.includes(s.toLowerCase()))) return false;
    }
    if (this._days.length) {
      const hay = getStr("Días de atención");
      if (!this._days.some((d) => hay.includes(d.toLowerCase()))) return false;
    }

    // Bounds solo si es Point
    if (this._bounds && f.geometry && f.geometry.type === "Point") {
      const [w, s, e, n] = this._bounds;
      const [lng, lat] = (f.geometry as Point).coordinates as [number, number];
      if (lng < w || lng > e || lat < s || lat > n) return false;
    }

    return true;
  }

  // ===== búsqueda =====
  onSearch(e: { target: { value: string } }) {
    const q = (e?.target?.value ?? "").toString().trim().toLowerCase();
    if (!q) return [];

    // Normalize accents for Spanish text
    const normalizeAccents = (text: string) => {
      return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    };

    const normalizedQuery = normalizeAccents(q);

    const feats = this._geojson?.features ?? [];

    // 1) Coincidencias por nombre
    const featureHitsByName = feats.filter((f) => {
      const p = f.properties || {};
      const nombre = (p["Nombre"] ?? p["name"] ?? "").toString();
      const normalizedNombre = normalizeAccents(nombre);
      return normalizedNombre.includes(normalizedQuery);
    });

    // 2) Coincidencias por "Servicio Ofrecido"
    const featureHitsByService = feats.filter((f) => {
      const p = f.properties || {};
      const servicio = (p["Servicio Ofrecido"] ?? "").toString();
      const normalizedServicio = normalizeAccents(servicio);
      return normalizedServicio.includes(normalizedQuery);
    });

    // Unir y eliminar duplicados conservando referencias originales
    const featureHits = Array.from(new Set([...featureHitsByName, ...featureHitsByService]));

    const serviceHits = [
      "Centro de Desarrollo Infantil",
      "Jardín / Guardería Infantil",
      "Escuela o Colegio",
      "Relevos en la limpieza de espacios",
      "Cuidado durante el día - Centro Día",
      "Lavandería",
      "Residencia",
      "Comedor Comunitario",
      "Transporte de poblaciones específicas",
      "Otros",
    ]
      .filter((s) => normalizeAccents(s).includes(normalizedQuery))
      .map((name) => ({ type: "Service" as const, name }));

    // Mostrar primero los servicios sugeridos (con lupa), luego los centros
    return [...serviceHits, ...featureHits];
  }
}

const colombiaMapService = new ColombiaMapService();
export default colombiaMapService;
